<script setup lang="ts">
import { ref } from 'vue'

import VueHcaptcha from '@hcaptcha/vue3-hcaptcha'
import { FormKitSchema, reset } from '@formkit/vue'
import { question } from '@/composables/questionSchema'
import InputMapLocations from '@/components/form/InputMapLocations.vue'
import { useGeoDataStore } from '~/stores/geoData'
import type { MatchmakingItem } from '~/types/types'
import { useStationDataStore } from '~/stores/stations'

const loadForm = ref(true)
onMounted(() => {
  setTimeout(() => {
    loadForm.value = false
  }, 1000)
})

defineExpose({
  resetForm,
})
const props = defineProps<{ data?: Partial<MatchmakingItem> | null }>()

const emit = defineEmits(['formSubmitted'])

const { keyValueOptions: operatorOptions } = await useKeyTranslator('operator')
const { keyValueOptions: typeOptions } = await useKeyTranslator('type')
const { keyValueOptions: offerOptions } = await useKeyTranslator('offer')
const { keyValueOptions: offerPublicActorOptions } = await useKeyTranslator('offerPublicActor')
const { keyValueOptions: requirementOptions } = await useKeyTranslator('requirementLabels')
// const { keyValueOptions: fuelTypeOptions } = await useKeyTranslator('fuelType')
const { keyValueOptions: accessibilityOptions } = await useKeyTranslator('accessibility')
const { keyValueOptions: vehicleTypeOptions } = await useKeyTranslator('vehicleType')
const { keyValueOptions: refillOptions } = await useKeyTranslator('refill')
const { keyValueOptions: capacityOptions } = await useKeyTranslator('capacity')

const data = ref<Partial<MatchmakingItem>>(props.data || {})
const hasExistingPost = !!props.data
const submitString = props.data ? 'Uppdatera' : 'Skapa inlägg'
const geoDataStore = useGeoDataStore()

await useAsyncData('counties', () => geoDataStore.initCounties())

const stationStore = useStationDataStore()
await useAsyncData('fuelTypes', () => stationStore.initFuelTypes())
const fuelTypeOptions = computed(() => {
  return stationStore.fuelTypes.map((fuelType) => {
    return {
      label: fuelType.title,
      value: fuelType.id,
    }
  })
})

const counties = computed(() =>
  geoDataStore.counties.map((county) => {
    return {
      label: county.name,
      value: county.county_code,
    }
  })
)

const mapInputChanged = (coordinateList: any[]) => {
  if (coordinateList.length > 0) {
    data.value.lat = coordinateList[0][1]
    data.value.lng = coordinateList[0][0]
  }
  data.value.locations = coordinateList.map((coordinate) => {
    return {
      lat: coordinate[1],
      lng: coordinate[0],
    }
  })
}

function resetForm() {
  reset('form')
}

function onSubmit(values: any) {
  if (!verified.value && !hasExistingPost) {
    showRecaptchaValidation.value = true
  } else {
    showRecaptchaValidation.value = false
    const payload = {
      ...values,
      capacity: values.capacity ? values.capacity.join() : null,
      fuel_type: Array.isArray(values.fuel_type) ? values.fuel_type.join() : null,
      vehicle_type: Array.isArray(values.vehicle_type) ? values.vehicle_type.join() : null,
    }
    emit('formSubmitted', payload)
  }
}
const verified = ref(false)
const expired = ref(false)
const token = ref('')
const eKey = ref('')
const error = ref('')
const showRecaptchaValidation = ref(false)

const onVerify = (tokenStr: string, ekey: string) => {
  verified.value = true
  token.value = tokenStr
  eKey.value = ekey
}
function onExpire() {
  verified.value = false
  token.value = ''
  eKey.value = ''
  expired.value = true
}

function onChallengeExpire() {
  verified.value = false
  token.value = ''
  eKey.value = ''
  expired.value = true
}

function onError(err: string) {
  token.value = ''
  eKey.value = ''
  error.value = err
}

watch(
  data,
  (newData) => {
    if (newData.start_date && !newData.end_date) {
      const originalDate = new Date(newData.start_date)
      const updatedDate = new Date(originalDate)
      updatedDate.setFullYear(originalDate.getFullYear() + 1)
      data.value.end_date = updatedDate as unknown as string
      const endDateObject = question.location[0].children[0].children[1]
      question.location[0].children[0].children[1] = {
        ...endDateObject,
        validation: `date_after:${originalDate}`,
      }
    }
  },
  { deep: true }
)
</script>
<template>
  <div>
    <AppLoadingWrapper :is-loading="data.operator && loadForm" :height="300">
      <FormKit
        id="form"
        v-model="data"
        type="form"
        :submit-label="submitString"
        :submit-attrs="{
          inputClass: 'p-button',
        }"
        incomplete-message="Vänligen fyll i alla obligatoriska fält för att skapa ditt inlägg."
        :config="{ validationVisibility: 'submit' }"
        @submit="onSubmit"
      >
        <FormKitSchema :schema="question.operator" :data="{ operatorOptions }" />
        <FormKitSchema
          v-if="data.operator === 'vehicle_owner'"
          :schema="question.type"
          :data="{ typeOptions }"
        />
        <FormKitSchema
          v-if="data.operator === 'fuel_supplier'"
          :schema="question.offer"
          :data="{ offerOptions }"
        />
        <FormKitSchema
          v-if="data.operator === 'public_actor'"
          :schema="question.offerPublicActor"
          :data="{ offerPublicActorOptions }"
        />
        <div class="flex row flex-wrap">
          <FormKitSchema
            v-if="data.operator === 'land_owner'"
            :schema="question.requirement"
            :data="{ requirementOptions }"
          />
          <div class="flex row flex-wrap">
            <FormKitSchema
              v-if="
                data.operator === 'vehicle_owner' ||
                data.offer === 'fuel' ||
                data.offer === 'internal_outlets' ||
                data.requirement === 'accessible_alternative_outlets' ||
                data.offer_public_actor === 'upcoming_requirements'
              "
              :schema="question.fuelType"
              :data="{ fuelTypeOptions }"
            />
            <FormKitSchema
              v-if="
                data.operator === 'vehicle_owner' ||
                data.offer === 'fuel' ||
                data.offer === 'internal_outlets'
              "
              :schema="question.fuelTypeVolumes"
            />
          </div>
          <div class="flex row flex-wrap">
            <FormKitSchema
              v-if="
                data.type === 'demand' ||
                data.offer === 'fuel' ||
                data.offer === 'charging_points' ||
                data.offer === 'internal_outlets'
              "
              :schema="question.accessibility"
              :data="{ accessibilityOptions }"
            />
            <FormKitSchema
              v-if="
                data.type === 'demand' || data.offer === 'fuel' || data.offer === 'internal_outlets'
              "
              :schema="question.refill"
              :data="{ refillOptions }"
            />
          </div>
          <FormKitSchema
            v-if="data.type === 'demand'"
            :schema="question.vehicle"
            :data="{ vehicleTypeOptions }"
          />
          <FormKitSchema
            v-if="data.offer === 'charging_points'"
            :schema="question.capacity"
            :data="{ capacityOptions }"
          />
        </div>
        <!-- <h3>Plats och tid</h3> -->
        <label class="formkit-label" for="input_19">Plats</label>
        <InputMapLocations :locations="data.locations" @mapinputchanged="mapInputChanged" />
        <FormKitSchema
          v-if="data.operator === 'public_actor'"
          :schema="question.locationPublicActor"
          :data="{ counties }"
        />
        <FormKitSchema
          v-else-if="data.operator === 'land_owner'"
          :schema="question.locationLandOwner"
          :data="{ counties }"
        />
        <FormKitSchema v-else :schema="question.location" :data="{ counties }" />
        <AppHeading title="Kontakt" type="h2" />
        <FormKitSchema :schema="question.contact" />
        <div v-if="!hasExistingPost">
          <FormKitSchema :schema="question.terms" />
          <div class="formkit-help terms-help">
            Genom att skapa inlägg intygar jag att informationen är korrekt samt samtycker till att
            informationen ovan publiceras på Drivmedla.se och att jag har tagit del av och godkänner
            <NuxtLink to="/villkor" target="_blank">Drivmedlas regler och villkor</NuxtLink>.
          </div>
          <vue-hcaptcha
            sitekey="98656e55-d840-45ad-beed-af768e883535"
            @verify="onVerify"
            @expired="onExpire"
            @challenge-expired="onChallengeExpire"
            @error="onError"
          />
          <p v-if="showRecaptchaValidation && !verified" class="formkit-message">
            Vänligen bekräfta att du inte är en robot för att skapa inlägget.
          </p>
        </div>
      </FormKit>
    </AppLoadingWrapper>
  </div>
</template>
<style scoped lang="scss">
.terms-help {
  margin-bottom: 1rem;
}
</style>
```
